/**
 * Maps the positions / approvals backend refusals (`{ error, code, message }`
 * from PositionsService.sendPositionsError and the onboarding controllers) to
 * translated copy. Unknown codes fall back to the server's own message, then
 * to the generic error — never a bare machine code.
 */

type TFunction = (key: string, options?: Record<string, unknown>) => string;

const CODE_KEYS: Record<string, string> = {
  POSITIONS_REQUIRED: "positions_required",
  POSITIONS_VIA_AMENDMENT: "positions_via_amendment_hint",
  POSITION_NAME_REQUIRED: "positions_name_lt_required",
  POSITION_NAME_TAKEN: "positions_error_name_taken",
  AREA_FORBIDDEN: "positions_error_area_forbidden",
  APPROVAL_NOT_PENDING: "ws_approvals_already_decided",
  WORK_SECURITY_APPROVALS_PENDING: "ws_error_approvals_pending",
  INVALID_GENDER: "invalid_gender",
  MERGE_PERMISSIONS_MASTER_ONLY: "positions_error_merge_permissions_master_only",
  NOT_POSITION_GRANTABLE: "positions_error_permission_not_grantable",
  UNKNOWN_PERMISSION: "perm_error_unknown",
};

/** The machine code of a failed request, if the server sent one. */
export function errorCode(error: any): string | null {
  const data = error?.response?.data;
  const code = (data && (data.code || data.error)) || null;
  return typeof code === "string" ? code : null;
}

/**
 * Sphere names a MERGE_AREA_FORBIDDEN refusal carries — accepted as
 * `areaNames: string[]` or `areas: (string | {name})[]`, whichever the
 * backend sends.
 */
export function refusedAreaNames(data: any): string[] {
  const raw = Array.isArray(data?.areaNames) ? data.areaNames : Array.isArray(data?.areas) ? data.areas : [];
  return raw
    .map((area: any) => (typeof area === "string" ? area : area?.name))
    .filter((name: any): name is string => typeof name === "string" && name.length > 0);
}

export function describePositionsError(error: any, t: TFunction): string {
  const data = error?.response?.data;
  const code = errorCode(error);
  if (code === "MERGE_AREA_FORBIDDEN") {
    const areas = refusedAreaNames(data);
    return areas.length
      ? t("positions_error_merge_area_forbidden", { areas: areas.join(", ") })
      : t("positions_error_merge_area_forbidden_plain");
  }
  if (code && CODE_KEYS[code]) return t(CODE_KEYS[code]);
  return (typeof data?.message === "string" && data.message) || code || t("error");
}
